import React from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components';
import {copyColor, copyFontFamily, copyFontSize, copyLineHeight, copyFontSizeSmall, copyFontSizeLarge, BreakpointType, PropsWithTheme} from '@nib-components/theme';
import {color as getColor, colorValues, validColorValues, map} from '@nib/layout';
import {Nullable, ResponsiveOrStaticProp, validAlignValues, validComponentValues, validWeightValues} from './utils';

type componentValues = (typeof validComponentValues)[number];
type alignValues = (typeof validAlignValues)[number];
type weightValues = (typeof validWeightValues)[number];
export const validSizeValues = ['small', 'medium', 'large'] as const;
type sizeValues = (typeof validSizeValues)[number];
type NamedColor = colorValues;
type OtherColor = ((props: PropsWithTheme) => string) | string;

export interface CopyProps {
  component?: componentValues;
  measure?: boolean;
  small?: boolean;
  large?: boolean;
  size?: ResponsiveOrStaticProp<Nullable<sizeValues>>;
  align?: ResponsiveOrStaticProp<Nullable<alignValues>>;
  color?: NamedColor | OtherColor;
  transform?: string;
  weight?: weightValues | number;
  [key: string]: unknown;
}

const BaseCopy: React.FC<CopyProps> = props => {
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const {component: Component = 'p', measure, small, large, size, align, color, weight, transform, ...otherProps} = props;
  return <Component data-mesh-component="COPY" {...otherProps} />;
};

const copyPropTypes = {
  component: PropTypes.oneOf(validComponentValues),
  measure: PropTypes.bool,
  small: PropTypes.bool,
  large: PropTypes.bool,
  align: PropTypes.oneOfType([
    PropTypes.oneOf(validAlignValues),
    PropTypes.shape({
      xs: PropTypes.oneOf(validAlignValues),
      sm: PropTypes.oneOf(validAlignValues),
      md: PropTypes.oneOf(validAlignValues),
      lg: PropTypes.oneOf(validAlignValues),
      xl: PropTypes.oneOf(validAlignValues),
      xxl: PropTypes.oneOf(validAlignValues),
      xxxl: PropTypes.oneOf(validAlignValues)
    })
  ]),
  size: PropTypes.oneOfType([
    PropTypes.oneOf(validSizeValues),
    PropTypes.shape({
      xs: PropTypes.oneOf(validSizeValues),
      sm: PropTypes.oneOf(validSizeValues),
      md: PropTypes.oneOf(validSizeValues),
      lg: PropTypes.oneOf(validSizeValues),
      xl: PropTypes.oneOf(validSizeValues),
      xxl: PropTypes.oneOf(validSizeValues),
      xxxl: PropTypes.oneOf(validSizeValues)
    })
  ]),
  color: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  weight: PropTypes.oneOfType([PropTypes.oneOf(validWeightValues), PropTypes.number]),
  transform: PropTypes.string
};

BaseCopy.propTypes = copyPropTypes;

const isNamedColor = (color?: OtherColor) => {
  if (!color) return false;
  if (validColorValues.includes(color as colorValues)) {
    return true;
  }
  return false;
};

const setColor: any = (props: CopyProps) => {
  if (isNamedColor(props.color)) {
    return getColor(props);
  }
  if (props.color !== 'inherit') {
    return css`
      color: ${props.color || copyColor};
    `;
  }
  return 'color: inherit';
};

const Copy = styled(({theme, ...rest}) => <BaseCopy {...rest} />).attrs(props => ({
  measure: true,
  align: 'left',
  color: 'inherit',
  small: false,
  large: false,
  weight: 'normal',
  ...props
}))<CopyProps>`
  margin: 0;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  ${props => setColor({...props, color: props.color!})};

  font-family: ${copyFontFamily};
  font-size: ${copyFontSize};
  line-height: ${copyLineHeight};

  ${props =>
    props.transform &&
    css`
      text-transform: ${props.transform};
    `}

  ${props =>
    props.measure &&
    css`
      max-width: 32em;
    `}

  ${({align, measure}) =>
    map(align, (val: alignValues) => {
      if (measure) {
        if (val === 'left')
          return css`
            text-align: ${val};
            margin-right: auto;
            margin-left: 0;
          `;

        if (val === 'center')
          return css`
            text-align: ${val};
            margin-left: auto;
            margin-right: auto;
          `;

        if (val === 'right')
          return css`
            text-align: ${val};
            margin-left: auto;
            margin-right: 0;
          `;
      } else if (align) {
        return css`
          text-align: ${val};
        `;
      }

      return '';
    })}

  ${props =>
    props.small &&
    css`
      font-size: ${copyFontSizeSmall};
    `}

  ${props =>
    props.large &&
    css`
      font-size: ${copyFontSizeLarge};
    `}
  
  ${props =>
    props.size &&
    !props.small &&
    !props.large &&
    map(props.size, (val: sizeValues) => {
      if (val === 'small') {
        return css`
          font-size: ${copyFontSizeSmall};
        `;
      } else if (val === 'large') {
        return css`
          font-size: ${copyFontSizeLarge};
        `;
      } else {
        return css`
          font-size: ${copyFontSize};
        `;
      }
    })}
`;

Copy.propTypes = copyPropTypes;

Copy.displayName = 'Copy';

export default Copy;

export const Italic = styled(Copy).attrs({
  component: 'em',
  'data-mesh-component': 'ITALIC'
})`
  font-style: italic;

  ${Copy} > & {
    font-size: inherit;
  }
`;

Italic.displayName = 'Italic';

export const Bold = styled(Copy).attrs({
  component: 'strong',
  'data-mesh-component': 'BOLD'
})`
  font-weight: ${props => props.weight};

  ${Copy} > & {
    font-size: inherit;
  }
`;

Bold.displayName = 'Bold';

Bold.propTypes = {
  weight: PropTypes.oneOfType([PropTypes.oneOf(['bold']), PropTypes.number])
};

Bold.defaultProps = {
  weight: 'bold'
};
